import { manageApi } from '@/api';
import { PlusIcon } from '@/components/icons';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button, ConfirmModal, InlineAlert } from '@/components/ui';
import { useTableActions } from '@/context/TablesContext';
import { CondimentFormModal } from '@/features/manage/components/CondimentFormModal';
import { CondimentManageRow } from '@/features/manage/components/CondimentManageRow';
import { ManageTable } from '@/features/manage/components/ManageTable';
import { useManagedList } from '@/features/manage/useManagedList';

const COLUMNS = [
  { label: 'Name' },
  { label: 'Cost price', align: 'right' as const },
  { label: 'Selling price', align: 'right' as const },
  { label: 'Order' },
];

/**
 * Admin: the condiment menu with cost and selling prices. Sales record both at the time of sale, so a price
 * change never rewrites past sales. Delete archives; past sales and bills keep the condiment.
 */
export function ManageCondimentsPage() {
  const { refresh } = useTableActions();
  const list = useManagedList(manageApi.condiments, refresh);
  const editing = list.editing;

  return (
    <>
      <PageHeader
        title="Manage condiments"
        aside={
          <Button variant="primary" icon={<PlusIcon size={16} />} onClick={list.startCreate}>
            Add condiment
          </Button>
        }
      />

      {list.error && <InlineAlert message={list.error} onDismiss={list.dismissError} />}

      <ManageTable columns={COLUMNS} loading={list.loading} isEmpty={list.items.length === 0} emptyText="No condiments yet. Add the first item on the menu.">
        {list.items.map((c) => (
          <CondimentManageRow key={c.id} condiment={c} onEdit={() => list.startEdit(c)} onDelete={() => list.startDelete(c)} />
        ))}
      </ManageTable>

      {editing && (
        <CondimentFormModal
          key={editing === 'new' ? 'new' : editing.id}
          condiment={editing === 'new' ? null : editing}
          busy={list.busy}
          error={list.modalError}
          onSave={list.save}
          onClose={list.close}
        />
      )}

      <ConfirmModal
        open={!!list.deleting}
        title="Delete condiment"
        message={<>Are you sure you want to delete <strong>{list.deleting?.name}</strong>?</>}
        confirmLabel="Delete"
        busyLabel="Deleting…"
        busy={list.busy}
        error={list.modalError}
        onConfirm={list.confirmDelete}
        onCancel={list.close}
      />
    </>
  );
}
